import { visionLabApi } from '@/app/apiClient';
import { normalizeAssetKeyQuery } from '@/features/assets/lib/assetKeyQuery';
import type { AssetSort, ProjectAssetsPage } from '@/features/assets/types/asset';
import type { DeliveryFilterOperator } from '@/features/delivery/types/contracts';
import type { ApiCallOptions } from '@/shared/api/client';
import type { InventoryFilters } from '../types/inventory';

/** Project-asset transport; project identity reads live in organization-project-picker. */

export interface FetchProjectAssetsParams extends InventoryFilters {
  limit?: number;
  cursor?: string | null;
  /**
   * Optional case-insensitive substring filter matched against the asset display
   * key or filename. Blank or absent requests the full project list (behavior
   * unchanged); a non-blank term filters within the project. Normalized (trim +
   * byte guard) before it is sent.
   */
  q?: string | null;
  /** Optional server ordering; `null` keeps the backend asset-ID default. */
  sort?: AssetSort;
  /** Omitted keeps all assets; true/false selects rejected/non-rejected assets. */
  rejected?: boolean;
  /** Omitted keeps all assets; true/false selects linked/unlinked Field Log footage. */
  fieldLogLinked?: boolean;
  /** Expands the current Field Log summary for every asset in the returned page. */
  includeFieldLog?: boolean;
  delivery?: string;
  deliveryOperator?: DeliveryFilterOperator;
}

type FetchProjectAssetsSearchParams = Omit<FetchProjectAssetsParams, 'includeFieldLog' | 'sort'> & {
  include?: readonly 'fieldLog'[];
  sort?: NonNullable<AssetSort>['field'];
  order?: NonNullable<AssetSort>['direction'];
};

/** Lists the assets owned by a project (`GET /projects/:pid/assets`), optionally filtered by key. */
export async function fetchProjectAssets(
  projectId: string,
  {
    limit,
    cursor,
    q,
    sort,
    rejected,
    delivery = 'all',
    deliveryOperator = 'in',
    mediaType,
    source,
    minDurationMs,
    maxDurationMs,
    fieldLogLinked,
    includeFieldLog = false,
  }: FetchProjectAssetsParams = {},
  options: ApiCallOptions = {},
): Promise<ProjectAssetsPage> {
  const normalizedQ = normalizeAssetKeyQuery(q);
  const searchParams: FetchProjectAssetsSearchParams = {
    mediaType: mediaType === 'all' ? undefined : mediaType,
    source,
    minDurationMs,
    maxDurationMs,
    limit,
    cursor: cursor || undefined,
    q: normalizedQ || undefined,
    sort: sort?.field,
    order: sort?.direction,
    rejected,
    fieldLogLinked,
    include: includeFieldLog ? ['fieldLog'] : undefined,
    delivery: delivery !== 'all' ? delivery : undefined,
    deliveryOperator:
      delivery !== 'all' && delivery !== 'none' && deliveryOperator === 'not_in'
        ? 'not_in'
        : undefined,
  };
  return visionLabApi.get<ProjectAssetsPage>(`/projects/${encodeURIComponent(projectId)}/assets`, {
    ...options,
    searchParams,
  });
}
